import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';

import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount, useUser } from '@feathr/extender/state';

import { useIframeMessaging } from '../../../hooks';
import { useDonationFormBuilder } from '../useDonationFormBuilder';
import { getEditorUrl } from './EditorIframe.utils';

import * as styles from './EditorIframe.css';

interface IEditorConfigMessage {
  type: 'feathr:editor:config';
  config: Record<string, unknown>;
  isDirty: boolean;
}

interface IEditorReadyMessage {
  type: 'feathr:editor:ready';
}

function isEditorConfigMessage(data: unknown): data is IEditorConfigMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:editor:config'
  );
}

function isEditorReadyMessage(data: unknown): data is IEditorReadyMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:editor:ready'
  );
}

function EditorIframe(): JSX.Element {
  const { donationForm, setEditorConfig } = useDonationFormBuilder();

  // Determine if user is a developer (same logic as Development & QA nav link)
  const account = useAccount();
  const user = useUser();
  const { isOnProduction } = useBlackboxVersion();
  const showDevelopmentNavUserSetting = user.getSetting('show_dev_nav') ?? false;
  const isDeveloper = !isOnProduction && (account.isTestAccount || showDevelopmentNavUserSetting);

  const { url: editorUrl, origin: editorOrigin } = getEditorUrl(isDeveloper);

  const sendConfigToEditor = useCallback((): void => {
    const config = toJS(donationForm.get('config') ?? {});

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'feathr:editor:init',
        config,
        formId: donationForm.id,
      },
      editorOrigin,
    );
  }, [donationForm, editorOrigin]);

  const handleMessage = useCallback(
    (event: MessageEvent): void => {
      if (isEditorReadyMessage(event.data)) {
        sendConfigToEditor();
        return;
      }

      if (isEditorConfigMessage(event.data)) {
        setEditorConfig(event.data.config, event.data.isDirty);
      }
    },
    [sendConfigToEditor, setEditorConfig],
  );

  const { iframeRef } = useIframeMessaging({
    origin: editorOrigin,
    onMessage: handleMessage,
    sendConfig: sendConfigToEditor,
  });

  return (
    <div className={styles.root}>
      <iframe
        className={styles.iframe}
        ref={iframeRef}
        src={editorUrl}
        title={'Donation Form Editor'}
      />
    </div>
  );
}

export default observer(EditorIframe);
